import React, { useState } from 'react';
import {
  Center,
  Icons,
  Modal,
  ModalBackdrop,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  VStack,
  useAlert,
} from '@tonic/central-specialties-ui-themed';
import { DB, type Schemas } from '@tonic/central-specialties-utils';
import {
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  Icon,
  Text,
} from '@gluestack-ui/themed';
import { useMutation } from '@tanstack/react-query';
import { downloadCSVFile } from '../../../../../utils/downloadObjectsAsCSV';

interface ImportNewEquipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Base64String = string;

const fileToBase64 = (file: File): Promise<Base64String> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result?.toString()?.split(',')[1];
      if (!base64) {
        reject(new Error('Failed to read file as base64'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });

export const ImportNewEquipmentModal = ({
  isOpen,
  onClose,
  onSuccess,
}: ImportNewEquipmentModalProps) => {
  const { alert } = useAlert();
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const importCSV = useMutation({
    mutationFn: async (base64String: string) => {
      const response = await DB.POST('/equipment/import', {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: {
          fileData: base64String,
        },
      });
      if (!response?.response?.ok) {
        const problem = response?.error as
          | Schemas['ProblemDetails']
          | undefined;
        throw new Error(
          problem?.detail ??
            problem?.title ??
            'Network response to upload equipment was not ok',
        );
      }
      return response.data as Schemas['EquipmentImportResult'];
    },
  });

  const selectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files && files[0]) {
      setFile(files[0]);
      setUploadStatus(files[0].name);
      setImportErrors([]);
    }
  };

  const uploadFile = async () => {
    if (!file) {
      setUploadStatus('No file selected');
      return;
    }

    setUploadStatus('Uploading…');
    setImportErrors([]);

    try {
      const base64Result = await fileToBase64(file);
      const result = await importCSV.mutateAsync(base64Result);

      const errors = result.errors ?? [];
      if (errors.length > 0) {
        setUploadStatus(
          `Imported ${result.succeeded} row(s) with ${errors.length} error(s)`,
        );
        setImportErrors(errors);
      } else {
        setUploadStatus(`Successfully imported ${result.succeeded} row(s)`);
        onSuccess?.();
      }
    } catch (error) {
      console.error('Error uploading file: ', error);
      setUploadStatus('');
      alert({
        status: 'error',
        message:
          error instanceof Error ? error.message : 'Failed to upload file',
        timeout: 10000,
      });
    }
  };

  const downloadFile = async () => {
    try {
      const response = await DB.GET('/equipment/export', {
        parseAs: 'text',
        headers: { Accept: 'text/plain' },
      });
      if (!response.data) {
        throw new Error('Network response was not ok');
      }
      downloadCSVFile(response.data, 'equipment.csv');
    } catch (error) {
      console.error('Download error:', error);
      alert({
        status: 'error',
        message:
          error instanceof Error ? error.message : 'Failed to download file',
        timeout: 10000,
      });
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadStatus('');
    setImportErrors([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalBackdrop />
      <ModalContent
        borderRadius={0}
        maxHeight="80%"
        w="$96"
        width="60%"
        maxWidth={800}
        minWidth={500}
      >
        <ModalHeader
          bg="$primary700"
          alignItems="center"
          justifyContent="space-between"
          py="$2"
          pl="$10"
          pr="$6"
        >
          <HStack flex={1} justifyContent="space-between" alignItems="center">
            <Text color="$white" size="lg">
              Import new equipment data
            </Text>
            <ModalCloseButton
              w="$8"
              justifyContent="center"
              alignItems="center"
            >
              <Icon color="$white" size="xl" as={Icons.X} />
            </ModalCloseButton>
          </HStack>
        </ModalHeader>
        <VStack space="lg" h="$full" mt="$4">
          <Center>
            <Text color="$black">
              {' '}
              Please use Select File below to choose a file to upload with the
              Import Equipment Data button. Or download the current Equipment
              Data list.
            </Text>
            {uploadStatus ? (
              <Text bold size="lg" color="$black">
                {`Upload Status of file: ${uploadStatus}`}
              </Text>
            ) : null}
            {importErrors.length > 0 && (
              <VStack mt="$2" space="xs" w="$full" px="$4">
                {importErrors.map((err, i) => (
                  <Text key={i} color="$error600" size="sm">
                    {err}
                  </Text>
                ))}
              </VStack>
            )}
          </Center>
          <Center flexDirection="row" mb="$4">
            <label
              style={{
                flex: 1,
                flexDirection: 'row',
                backgroundColor: '#9D0101',
                color: '#FFFFFF',
                alignContent: 'center',
                justifyContent: 'center',
                borderRadius: 4,
                marginRight: 16,
                paddingLeft: 32,
                paddingRight: 0,
                gap: 8,
                height: 48,
                maxWidth: 120,
                cursor: 'pointer',
                fontSize: 16,
              }}
            >
              Select File
              <input
                type="file"
                accept=".csv"
                onChange={selectFile}
                style={{ display: 'none' }}
              />
            </label>
            <Button
              size="lg"
              onPress={uploadFile}
              gap="$2"
              isDisabled={!file || importCSV.isPending}
            >
              <ButtonIcon
                as={importCSV.isPending ? Icons.Loader : Icons.Upload}
              />
              <ButtonText>
                {importCSV.isPending ? 'Uploading…' : 'Import Equipment Data'}
              </ButtonText>
            </Button>
            <Button size="lg" onPress={downloadFile} gap="$2" ml={'$2'}>
              <ButtonIcon as={Icons.Download} />
              <ButtonText>Download Equipment Data</ButtonText>
            </Button>
          </Center>
        </VStack>
      </ModalContent>
    </Modal>
  );
};
